/**
 * Auth store — Zustand bilan
 *
 * State:
 *   - user: UserDto | null
 *   - token: string | null
 *   - isAuthenticated: computed
 *
 * Actions:
 *   - login(token, user)
 *   - logout()
 *   - updateUser(partial)
 *
 * Persistence:
 *   - localStorage'ga saqlash (token, user) — Faza 4 da
 *   - Faza 9 (offline) — Dexie'ga ham backup
 */

import type { AuthTokenPayload, UserDto } from '@avtofix/shared';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { apiClient } from '@/shared/lib/api-client';
import { toast } from '@/shared/ui/toast';

interface AuthState {
  user: UserDto | null;
  token: string | null;
  tokenPayload: AuthTokenPayload | null;

  // Computed
  isAuthenticated: () => boolean;
  isRole: (role: AuthTokenPayload['role']) => boolean;

  // Actions
  login: (params: { token: string; user: UserDto; payload: AuthTokenPayload }) => void;
  logout: () => void;
  updateUser: (partial: Partial<UserDto>) => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      immer((set, get) => ({
        user: null,
        token: null,
        tokenPayload: null,

        isAuthenticated: (): boolean => {
          const { token, tokenPayload } = get();
          if (!token || !tokenPayload) {
            return false;
          }
          // Token muddati tugaganmi tekshirish
          const now = Math.floor(Date.now() / 1000);
          return tokenPayload.exp > now;
        },

        isRole: (role): boolean => {
          const { tokenPayload } = get();
          return tokenPayload?.role === role;
        },

        login: ({ token, user, payload }) => {
          set((state) => {
            state.user = user;
            state.token = token;
            state.tokenPayload = payload;
          });
          apiClient.setToken(token);
        },

        logout: () => {
          set((state) => {
            state.user = null;
            state.token = null;
            state.tokenPayload = null;
          });
          apiClient.setToken(null);
        },

        updateUser: (partial) => {
          set((state) => {
            if (state.user) {
              Object.assign(state.user, partial);
            }
          });
        },
      })),
      {
        name: 'avtofix-auth',
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          tokenPayload: state.tokenPayload,
        }),
        onRehydrateStorage: () => (state) => {
          // Sahifa yangilanganda apiClient'ga token qaytadan yuklash
          if (state?.token) {
            apiClient.setToken(state.token);
          }
        },
      },
    ),
    { name: 'AuthStore' },
  ),
);

// 401 xatosida avtomatik logout + toast + redirect (api-client.ts'dagi window event)
// M9 fix: sessiya muddati tugasa user ko'radi
if (typeof window !== 'undefined') {
  window.addEventListener('auth:unauthorized', () => {
    const { token } = useAuthStore.getState();
    // Faqat login qilgan user uchun ishlaydi (401 login so'rovida ham kelishi mumkin)
    if (token !== null) {
      useAuthStore.getState().logout();
      toast.warning('Sessiya muddati tugadi. Qayta kiring.');
      // React Router mavjud emas — to'g'ridan-to'g'ri location'ni yangilaymiz
      // (BrowserRouter tashqarisida, shuning uchun window.location ishlatamiz)
      window.location.replace('/login?reason=session-expired');
    }
  });
}
